import type { PlayInfo } from '@module/apis/client-side/live/type';
import type { useFetchLivePlayMutation } from '@module/queries/live/liveQueryHooks';
import type { GoPlayParams } from '@module/types/play/goPlayTypes';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { DEFAULT_EXCEL_SELECT_VALUE, useBroadcastSponPopoverStore } from '../../stores/useBroadcastSponPopoverStore';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useGetLoginInfoQuery from '../auth/useGetLoginInfoQuery';

/** /play 진입/재진입 시 goPlay에 넘길 미디어 파라미터를 만든다. pw가 있으면 비번방으로 표시한다. */
const buildDirectEntryMedia = (userId: string, pw?: string): GoPlayParams => ({
  userId,
  userNick: '',
  isLive: true,
  isAdult: false,
  isPw: !!pw,
  pw,
  type: 'free',
  user: 0,
  userLimit: Infinity,
});

interface Props {
  goPlay: (
    media: GoPlayParams,
    mutateAsyncParams?: Partial<Parameters<ReturnType<typeof useFetchLivePlayMutation>['mutateAsync']>[0]>,
  ) => Promise<PlayInfo | undefined>;
  userId: string;
  enabled?: boolean;
}
/**
 * @description /play/[userId] 계열 라우트에 진입할 때마다 goPlay로 playInfo를 보장하는 훅.
 * 썸네일 클릭처럼 상위 플로우에서 이미 goPlay가 호출된 자연스러운 진입 케이스는 store 값을 그대로 사용하고,
 * 새로고침 또는 URL 직접 입력처럼 goPlay 없이 진입한 케이스만 보완적으로 처리한다.
 * BFCache 뒤로가기 복원도 "재진입"으로 간주해 play를 강제 재요청한다(아래 pageshow 이펙트 참고).
 */
const useAutoGoPlayOnDirectEntry = ({ goPlay, userId, enabled = true }: Props) => {
  const searchParams = useSearchParams();
  const isShareLink = searchParams.get('sl') === '1';
  // 새 창 진입 시 goPlay가 pw 없이 재시도해 needPw 에러가 뜨는 것을 막기 위해
  // 발신 측(useGoPlay)이 URL에 실어 보낸 비밀번호를 읽는다.
  const pendingPw = searchParams.get('pw') ?? undefined;
  const { data: playInfo } = useFetchLivePlayMutationStore();
  const { isFetched: isLoginInfoFetched } = useGetLoginInfoQuery();
  const setDefaultExcelSelectValue = useBroadcastSponPopoverStore((state) => state.setDefaultExcelSelectValue);

  // goPlay는 매 렌더 새로 생성되므로 pageshow 리스너를 매번 재등록하지 않도록 ref로 최신값을 참조한다.
  const goPlayRef = useRef(goPlay);
  goPlayRef.current = goPlay;

  if (Array.isArray(userId)) throw new Error('userId must be a string');

  // 읽는 즉시 URL에서 제거 — 새로고침 시 재사용되지 않도록.
  useEffect(() => {
    if (!pendingPw) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('pw');
    window.history.replaceState({}, '', url.pathname + (url.search || ''));
  }, []);

  // 헤더 로고(순수 <a> 하드내비) 등으로 이탈 후 뒤로가기로 복귀하면 브라우저 BFCache가 문서를 통째로
  // 복원한다. React는 살아있지만 진입 이펙트가 재실행되지 않아 /v1/live/play 재검증이 스킵된다(비번방 우회).
  // reload 없이, store를 비우고 goPlay를 직접 태워 직접 진입과 동일하게 재요청한다.
  //  - reset()으로 stale playInfo를 먼저 지워, 재검증 완료 전까지 stale 콘텐츠 노출/후원 진행을 막는다.
  //    (진행 중이던 연속/두산 후원 루프도 store가 비면 useAutoSpon의 isBroadcastLeft 가드로 즉시 중단.)
  //  - 새 playInfo가 store에 반영되면 소켓·플레이어 등 dep 기반 이펙트가 정상 재초기화된다.
  useEffect(() => {
    const revalidateOnBfcacheRestore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (!enabled || !userId) return;
      if (!window.location.pathname.includes('/play/')) return;

      useFetchLivePlayMutationStore.getState().reset();
      // 복원 시엔 pw를 실지 않아(isPw:false) 비번방이면 서버 needPw로 재인증되게 한다.
      goPlayRef.current(buildDirectEntryMedia(userId));
    };
    window.addEventListener('pageshow', revalidateOnBfcacheRestore);
    return () => window.removeEventListener('pageshow', revalidateOnBfcacheRestore);
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled) return;
    if (!userId) return;
    if (!isLoginInfoFetched) return;
    const playInfoUserId = playInfo?.media.userId;
    if (playInfoUserId === userId) {
      // If the userId's playInfo is already called, we don't need to call goPlay again
      return;
    }

    const directEntryMedia = buildDirectEntryMedia(userId, pendingPw);

    const mutateAsyncParams: Parameters<typeof goPlay>[1] = {};
    if (isShareLink) mutateAsyncParams.shareLinkType = 'shareLink';

    goPlay(directEntryMedia, mutateAsyncParams);
  }, [enabled, userId, isLoginInfoFetched]);

  useEffect(() => {
    return () => {
      if (userId && userId === useFetchLivePlayMutationStore.getState().data?.media.userId) {
        useFetchLivePlayMutationStore.getState().reset();
      }
      setDefaultExcelSelectValue(DEFAULT_EXCEL_SELECT_VALUE);
    };
  }, [userId]);

  return {
    playInfo,
  };
};

export default useAutoGoPlayOnDirectEntry;
